import { useEffect, useRef, useState, memo } from 'react';
import { healthStats } from '../../../mocks/journey';

const AnimatedNumber = memo(({ value, suffix, isVisible }: { value: number; suffix: string; isVisible: boolean }) => {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!isVisible || hasRun.current) return;
    hasRun.current = true;

    const duration = 1600;

    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) startTimeRef.current = currentTime;
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * value));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCount(value);
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [isVisible, value]);

  return <span>{count}{suffix}</span>;
});

export default function Statistics() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-0 bg-[#0d0d0d] section-offscreen">
      <div className="max-w-7xl mx-auto px-8">
        <div className="border-t border-b border-white/8 grid grid-cols-2 lg:grid-cols-4">
          {healthStats.map((stat, index) => (
            <div
              key={index}
              className={`py-16 px-8 flex flex-col items-center gap-4 text-center ${
                index === 1 || index === 3 ? '' : 'border-r border-white/8'
              } ${index < 2 ? 'border-b lg:border-b-0 border-white/8' : ''}`}
            >
              <div className="w-12 h-12 flex items-center justify-center">
                <i className={`${stat.icon} text-3xl text-[#c8a96e]`} aria-hidden="true" />
              </div>
              <div className="text-4xl sm:text-6xl font-black text-white">
                <AnimatedNumber value={stat.value} suffix={stat.suffix} isVisible={isVisible} />
              </div>
              <div className="text-xs text-gray-500 uppercase tracking-[0.2em] font-semibold">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
